import React, { useState, useEffect } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import Swal from "sweetalert2";
import viewIcon from "../assets/Images/View.png";
import editIcon from "../assets/Images/Edit.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";
import filterIcon from "../assets/Images/Filter.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import usersIcon from "../assets/icons/departmentw.svg";
import { useDispatch } from "react-redux";
import { triggerRefresh } from "../redux/refreshSlice";

const BASE_URL = `${process.env.REACT_APP_API_URL}/departments`;
const ENTERPRISE_URL = `${process.env.REACT_APP_API_URL}/enterprises/all`;

const DepartmentManagement = () => {
  const dispatch = useDispatch();

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // ================= STATE (SAME PATTERN) =================
  const [departments, setDepartments] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [departmentCounts, setDepartmentCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [formData, setFormData] = useState({
    department_name: "",
    department_description: "",
    enterprise_fid: "",
  });

  // ================= FETCH =================
  const fetchDepartments = async () => {
    try {
      const res = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepartments(res.data);
    } catch {
      showErrorToast(TOAST_MESSAGES.DEPARTMENT.FETCH_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`${BASE_URL}/counts/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepartmentCounts(res.data);
    } catch {
      showErrorToast(TOAST_MESSAGES.DEPARTMENT.STATS_ERROR);
    }
  };

  const fetchEnterprises = async () => {
    try {
      const res = await api.get(ENTERPRISE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnterprises(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchStats();
    fetchEnterprises();
  }, []);

  // ================= FILTER + SEARCH (SAME LOGIC) =================
  const filteredDepartments = departments
    .filter((d) =>
      d.department_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((d) => {
      if (!dayFilter) return true;
      const createdDate = new Date(d.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dayFilter === "Today") return createdDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return createdDate >= yesterday && createdDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  // ================= PAGINATION (UNCHANGED) =================
  const totalEntries = filteredDepartments.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredDepartments.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  // ================= ACTIONS =================
  const resetForm = () => {
    setFormData({
      department_name: "",
      department_description: "",
      enterprise_fid: "",
    });
    setEditingId(null);
    setViewMode(false);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingId) {
        await api.put(`${BASE_URL}/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSuccessToast("Department updated successfully");
      } else {
        await api.post(BASE_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSuccessToast("Department created successfully");
      }

      resetForm();
      fetchDepartments();
      fetchStats();
      dispatch(triggerRefresh());
    } catch {
      showErrorToast(
        editingId
          ? TOAST_MESSAGES.DEPARTMENT.UPDATE_ERROR
          : TOAST_MESSAGES.DEPARTMENT.CREATE_ERROR
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (d) => {
    setFormData({
      department_name: d.department_name,
      department_description: d.department_description,
      enterprise_fid: d.enterprise_fid,
    });
    setEditingId(d.department_id);
    setShowForm(true);
  };

  const handleView = (d) => {
    setFormData({
      department_name: d.department_name,
      department_description: d.department_description,
      enterprise_fid: d.enterprise_fid,
    });
    setViewMode(true);
    setShowForm(true);
  };

  const handleStatusChange = async (d) => {
    try {
      await api.patch(
        `${BASE_URL}/${d.department_id}/status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDepartments();
      fetchStats();
      showSuccessToast("Department status updated");
    } catch {
      showErrorToast(TOAST_MESSAGES.DEPARTMENT.STATUS_CHANGE_ERROR);
    }
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // ================= STATS DATA (SAME BOX UI) =================
  const statsData = [
    {
      label: "Total Departments",
      value: departmentCounts.total || 0,
      summary: [
        { label: "Active", value: departmentCounts.active || 0 },
        { label: "Inactive", value: departmentCounts.inactive || 0 },
      ],
      icon: usersIcon,
    },
    {
      label: "Active Departments",
      value: departmentCounts.active || 0,
      summary: [{ label: "Total", value: departmentCounts.total || 0 }],
      icon: usersIcon,
    },
    {
      label: "Inactive Departments",
      value: departmentCounts.inactive || 0,
      summary: [{ label: "Total", value: departmentCounts.total || 0 }],
      icon: usersIcon,
    },
  ];

  // ================= JSX (UNCHANGED STRUCTURE) =================
  return (
    <div className="mx-2 pt-1 h-100">
      {/* ===== STATS ===== */}
      <div className="d-flex flex-wrap gap-3 mb-2 mt-0 justify-content-evenly align-items-center">
        {statsData.map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-1">
              <img src={item.icon} className="icon-image1" alt="" />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{item.value}</h2>
            {/* <div className="box-summary">
              {item.summary.map((s, i) => (
                <span key={i}>
                  {s.label}: {s.value}
                </span>
              ))}
            </div> */}
          </div>
        ))}
      </div>

      {/* ===== HEADER ===== */}
      <div className="d-flex justify-content-between align-items-center mb-2 mt-1">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          Department Management Details
        </span>
      </div>

      {/* ===== SEARCH / FILTER / ADD (SAME UI) ===== */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Departments"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div>
            <button
              className="icon-button filter-dropdown-toggle action-button"
              data-bs-toggle="dropdown"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div className="dropdown-menu profile-menu-dropdown p-2">
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label">
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label">{day || "All"}</label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => setCurrentPage(1)}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button ms-2"
            onClick={() => setShowForm(true)}
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          {/* ===== TABLE ===== */}
          <div className="table-responsive">
            <table className="table user-table1 table-sm w-100">
              <thead>
                <tr>
                  <th>Department Name</th>
                  <th>Enterprise</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((d, i) => (
                  <tr key={i}>
                    <td>{d.department_name}</td>
                    <td>
                      {
                        enterprises.find(
                          (e) => e.enterprise_id === d.enterprise_fid
                        )?.enterprise_name
                      }
                    </td>
                    <td>{d.department_description || "—"}</td>
                    <td>
                      {d.department_status === "1" ? "Active" : "Inactive"}
                    </td>
                    <td>{formatDateToUserTimeZone(d.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(d)}
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(d)}
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleStatusChange(d)}
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ===== PAGINATION FOOTER (UNCHANGED) ===== */}
          {/* <div className="d-flex justify-content-between align-items-center mt-2"> */}
          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* ===== MODAL (SAME STRUCTURE) ===== */}
      {showForm && (
        <div className="modal modal-backdrop-custom d-block">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleSubmit}>
                <div className="modal-header justify-content-center">
                  <h5 className="modal-title">
                    {viewMode
                      ? "View Department Details"
                      : editingId
                      ? "Update Department Details"
                      : "Add Department Details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={resetForm}
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label required-label">
                        Enterprise
                      </label>
                      <select
                        className="form-select"
                        disabled={viewMode}
                        value={formData.enterprise_fid}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise_fid: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Enterprise</option>
                        {enterprises
                          .filter((e) => e.enterprise_status === "1")
                          .map((e) => (
                            <option
                              key={e.enterprise_id}
                              value={e.enterprise_id}
                            >
                              {e.enterprise_name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label required-label">
                        Department Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        disabled={viewMode}
                        value={formData.department_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      disabled={viewMode}
                      value={formData.department_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          department_description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancle-btn"
                    onClick={resetForm}
                  >
                    {viewMode ? "Close" : "Cancel"}
                  </button>

                  {!viewMode && (
                    <button
                      type="submit"
                      className="custom-btn"
                      disabled={isSubmitting}
                    >
                      {editingId ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
